/**
 * m3 echo Frontend - Benutzer-Dialog
 *
 * Dialog zum Erstellen oder Bearbeiten eines Benutzers.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Box,
  MenuItem,
  Typography,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { User, UserCreate, UserUpdate, Tenant } from '../../services/admin';

interface UserDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: UserCreate | UserUpdate) => Promise<{ temporary_password?: string }>;
  user?: User | null;
  tenants: Tenant[];
  mode: 'create' | 'edit';
}

export const UserDialog: React.FC<UserDialogProps> = ({
  open,
  onClose,
  onSave,
  user,
  tenants,
  mode,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState<UserCreate>({
    email: '',
    full_name: '',
    tenant_id: undefined,
    is_admin: false,
    role: 'editor',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  // Load user data when editing
  useEffect(() => {
    if (mode === 'edit' && user) {
      setFormData({
        email: user.email,
        full_name: user.full_name || '',
        tenant_id: user.tenant_id || undefined,
        role: user.role || 'editor',
      });
    } else {
      setFormData({
        email: '',
        full_name: '',
        tenant_id: undefined,
        is_admin: false,
        role: 'editor',
      });
    }
    setError(null);
    setTempPassword(null);
  }, [mode, user, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    setTempPassword(null);

    try {
      if (mode === 'create') {
        const result = await onSave(formData);
        if (result.temporary_password) {
          setTempPassword(result.temporary_password);
        } else {
          onClose();
        }
      } else {
        // For edit mode, only send changed fields
        const updateData: UserUpdate = {
          full_name: formData.full_name,
          tenant_id: formData.tenant_id,
          role: formData.role,
        };
        await onSave(updateData);
        onClose();
      }
    } catch (err: any) {
      console.error('Fehler beim Speichern des Benutzers:', err);
      setError(err.message || 'Fehler beim Speichern des Benutzers');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTempPassword(null);
    onClose();
  };

  // Wenn wir ein temporäres Passwort haben, zeige es anstelle des Formulars
  if (tempPassword) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Benutzer Erfolgreich Erstellt</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            Benutzer wurde mit einem temporären Passwort erstellt.
          </Alert>
          <Typography variant="body2" gutterBottom>
            Bitte geben Sie dieses temporäre Passwort an den Benutzer weiter:
          </Typography>
          <Paper
            sx={{
              p: 2,
              mt: 2,
              bgcolor: 'grey.100',
              fontFamily: 'monospace',
              fontSize: '1.1rem',
              textAlign: 'center',
            }}
          >
            {tempPassword}
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Der Benutzer muss dieses Passwort bei der ersten Anmeldung ändern.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {mode === 'create' ? 'Benutzer Erstellen' : 'Benutzer Bearbeiten'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* E-Mail - nur beim Erstellen bearbeitbar */}
            <TextField
              label="E-Mail"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              required
              disabled={mode === 'edit'}
              helperText={mode === 'edit' ? 'E-Mail kann nicht geändert werden' : ''}
              fullWidth
            />

            {/* Vollständiger Name */}
            <TextField
              label="Vollständiger Name"
              value={formData.full_name}
              onChange={(e) =>
                setFormData({ ...formData, full_name: e.target.value })
              }
              helperText="Optionaler Anzeigename"
              fullWidth
            />

            {/* Mandanten-Auswahl */}
            <TextField
              select
              label="Mandant"
              value={formData.tenant_id || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  tenant_id: e.target.value ? parseInt(e.target.value) : undefined,
                })
              }
              helperText="Leer lassen für reine Admin-Benutzer"
              fullWidth
            >
              <MenuItem value="">
                <em>Kein (Nur Admin)</em>
              </MenuItem>
              {tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.display_name}
                </MenuItem>
              ))}
            </TextField>

            {/* Rolle - nur für Nicht-Admin-Benutzer */}
            {!formData.is_admin && (
              <TextField
                select
                label="Rolle"
                value={formData.role || 'editor'}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    role: e.target.value as 'editor' | 'viewer',
                  })
                }
                helperText="Editor: Vollzugriff auf Projekte. Betrachter: Nur Lesezugriff."
                fullWidth
              >
                <MenuItem value="editor">Editor</MenuItem>
                <MenuItem value="viewer">Betrachter</MenuItem>
              </TextField>
            )}

            {/* Admin toggle - only shown in create mode (cannot change via edit) */}
            {mode === 'create' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_admin}
                    onChange={(e) =>
                      setFormData({ ...formData, is_admin: e.target.checked })
                    }
                  />
                }
                label="Administrator"
              />
            )}

            {mode === 'create' && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Ein temporäres Passwort wird generiert und nach der Erstellung angezeigt.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {mode === 'create' ? 'Erstellen' : 'Speichern'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserDialog;
